import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { InfoTip } from "@client/components/InfoTip";
import { labelText, type HintPlacement } from "@client/lib/fieldHints";

/**
 * The form controls, sharing one frame so a label, a hint and an error always
 * sit in the same places and are always wired to the input with `id` /
 * `aria-describedby` rather than by being next to it on screen.
 *
 * The editor's `MoneyField` / `PriceField` / `CountField` (phase 6) build on the
 * same frame; these are the plain ones the product and scenario forms need.
 */

export const inputClass =
  "w-full rounded-md border border-line bg-chip-bg px-3 py-2 text-base text-ink placeholder:text-ink-soft/70 disabled:cursor-not-allowed disabled:opacity-60";

interface FrameProps {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  hintPlacement?: HintPlacement;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
}

function FieldFrame({
  id,
  label,
  hint,
  hintPlacement = "tip",
  error,
  required,
  children,
}: FrameProps) {
  // The hint is the same sentence either way and keeps the same id, so
  // `aria-describedby` does not care which placement a caller picked.
  const asTip = hint && !error && hintPlacement === "tip";
  const asProse = hint && !error && hintPlacement === "below";

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <label htmlFor={id} className="text-sm font-semibold text-ink">
          {label}
          {required && (
            <span className="ml-1 text-ink-soft" aria-hidden="true">
              *
            </span>
          )}
        </label>
        {asTip && (
          <InfoTip id={`${id}-hint`} label={labelText(label)}>
            {hint}
          </InfoTip>
        )}
      </div>
      {children}
      {asProse && (
        <p id={`${id}-hint`} className="text-xs text-ink-soft">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={`${id}-error`}
          className="text-xs font-semibold"
          style={{ color: "var(--chart-crit)" }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

/** `aria-describedby` points at whichever of the two is on screen, never both. */
function describedBy(id: string, hint: ReactNode, error: string | null | undefined) {
  if (error) return `${id}-error`;
  if (hint) return `${id}-hint`;
  return undefined;
}

type FieldExtras = {
  label: ReactNode;
  hint?: ReactNode;
  hintPlacement?: HintPlacement;
  error?: string | null;
};

export function TextField({
  label,
  hint,
  hintPlacement,
  error,
  className = "",
  id,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & FieldExtras) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <FieldFrame
      id={fieldId}
      label={label}
      hint={hint}
      hintPlacement={hintPlacement}
      error={error}
      required={props.required === true}
    >
      <input
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(fieldId, hint, error)}
        className={`${inputClass} ${className}`}
        {...props}
      />
    </FieldFrame>
  );
}

export function NumberField({
  label,
  hint,
  hintPlacement,
  error,
  className = "",
  id,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & FieldExtras) {
  return (
    <TextField
      label={label}
      hint={hint}
      hintPlacement={hintPlacement}
      error={error}
      id={id}
      type="number"
      // Numbers on a phone deserve the numeric keypad, and a pasted "1,200"
      // should not be silently discarded by the browser's own parser.
      inputMode="decimal"
      className={`tabular ${className}`}
      {...props}
    />
  );
}

export function TextAreaField({
  label,
  hint,
  hintPlacement,
  error,
  className = "",
  id,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & FieldExtras) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <FieldFrame
      id={fieldId}
      label={label}
      hint={hint}
      hintPlacement={hintPlacement}
      error={error}
      required={props.required === true}
    >
      <textarea
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(fieldId, hint, error)}
        className={`${inputClass} resize-y ${className}`}
        {...props}
      />
    </FieldFrame>
  );
}

export function SelectField({
  label,
  hint,
  hintPlacement,
  error,
  className = "",
  id,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & FieldExtras) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <FieldFrame
      id={fieldId}
      label={label}
      hint={hint}
      hintPlacement={hintPlacement}
      error={error}
      required={props.required === true}
    >
      <select
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(fieldId, hint, error)}
        className={`${inputClass} ${className}`}
        {...props}
      >
        {children}
      </select>
    </FieldFrame>
  );
}
